import { Data, UI, Utils } from 'common';
import isString from 'lodash/isString';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import { CancelAccountContext } from './CancelAccount.context';
import {
	CancelQuestionsDropdown1,
	CancelQuestionsDropdown2,
	CancelQuestionsDropdown3,
	CancelQuestionsDropdown4,
	CancelReasonForRetention,
	getCancelQuestionsDropdown1CanonicalResponse,
	NewMailingMethod,
} from './CancelAccount.enum';

const { States } = Data;
const {
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedButton,
	InlineMessage,
	Link,
} = UI;
const { Logger, Session, withLayoutAndModalContexts, Analytics, Util } = Utils;

class AddressVerification extends Component {
	cancelAccountContext = null;

	constructor() {
		super();

		const { customerDetails = {}, planDetails = {} } = Session.getAccountInfo();
		const address = customerDetails.address || {};
		const name = address?.name || '';
		const [firstName = '', lastName = ''] = name.split(' ');

		this.state = {
			address: {
				firstName,
				lastName,
				companyName: address.companyName,
				addressLine1: isString(address.addressLine1)
					? address.addressLine1
					: '',
				addressLine2: isString(address.addressLine2)
					? address.addressLine2
					: '',
				city: address.city,
				stateProvince: address.stateProvince,
				postalCode: address.postalCode,
				phone: address.phone,
			},
			validations: {
				// 0 = untouched, -1 = failed, 1 = valid
				addressLine1: 0,
				city: 0,
				stateProvince: 0,
				postalCode: 0,
			},
			showConfirmation: false,
			mask: false,
			currentPlanId: planDetails.planId,
		};

		Session.setFormStateDirty(false);
	}

	onFieldChange = e => {
		const { value, name } = e.target;
		const { address } = this.state;

		this.setState({
			address: {
				...address,
				[name]: value,
			},
		});

		Session.setFormStateDirty(true);
	};

	onInputTextBlur = e => {
		const { name } = e.target;
		const { address, validations } = this.state;

		this.setState({
			validations: {
				...validations,
				[name]: address[name] !== '' ? 1 : -1,
			},
		});
	};

	onConfirmAddressClick = () => {
		this.closeAccount();
	};

	closeAccount = async () => {
		const {
			Dropdown1Answer,
			Dropdown2Answer,
			Dropdown2Other,
			Dropdown3Answer,
			saveCancelAccountInfo,
			optimizely,
			offeredPlanId,
		} = this.cancelAccountContext;
		const {
			modalContext: { showError },
			history,
		} = this.props;
		const { address, currentPlanId } = this.state;
		const { CurrentServiceFees } = Session.getInternalAccountInfo();
		const closeAccountData = {
			ReturnAddress:
				address.addressLine1 +
				(address.addressLine2 !== '' ? ` ${address.addressLine2}` : ''),
			ReturnCity: address.city,
			ReturnState: address.stateProvince,
			ReturnZip: address.postalCode,
			FuturePostageNeeds: CancelQuestionsDropdown2[Dropdown2Answer],
			OtherFuturePostageNeeds: CancelQuestionsDropdown2[Dropdown2Other],
			CustomerServiceId: '',
			SendEmail: true,
			Disposition: {
				Id: 1166, // id used when closing account
				Description: null,
			},
			NewMailingMethod: {
				Id: NewMailingMethod[Dropdown3Answer] || '',
				Description: this.getNewMailingMethodReason(),
			},
			CancelReason: {
				Id: CancelReasonForRetention[Dropdown1Answer],
				Description:
					getCancelQuestionsDropdown1CanonicalResponse(Dropdown1Answer),
			},
		};

		Logger.saveActionData('Confirm Address Button Clicked');

		if (this.isAddressValid()) {
			this.setState({
				mask: true,
			});

			const cancelPlanResponse =
				await AccountCommunication.closeAccount(closeAccountData);

			if (AccountCommunication.isSuccessful(cancelPlanResponse)) {
				const abTestProperties = {
					offeredPlanId,
					currentPlanId,
					currentPlanPrice: CurrentServiceFees,
					dropdown1Answer: Dropdown1Answer,
					dropdown2Answer: Dropdown2Answer,
					dropdown2Other: Dropdown2Other,
					dropdown3Answer: Dropdown3Answer,
				};

				optimizely.track('cancel_account_canceled', abTestProperties);
				Analytics.trackEvent(
					'cancel_account_canceled',
					abTestProperties,
					Analytics.agent.segment
				);

				saveCancelAccountInfo({
					CanceledDate: new Date().toUTCString(),
				});

				Session.setFormStateDirty(false);

				// Navigate to change plan page
				await Session.loadAccountInfo();

				this.setState({
					mask: false,
				});

				history.push(RoutePath.changePlan);
			} else {
				showError({
					response: cancelPlanResponse,
				});
			}
		}
	};

	getNewMailingMethodReason = () => {
		const { Dropdown2Answer, Dropdown3Answer } = this.context;

		if (Dropdown2Answer === 'multiCarrier') {
			return CancelQuestionsDropdown3[Dropdown3Answer];
		} else if (Dropdown2Answer === 'marketplace') {
			return CancelQuestionsDropdown4[Dropdown3Answer];
		}

		return '';
	};

	/**
	 * Return `true` if all required fields are valid.
	 */
	isAddressValid = () => {
		let isValid = true;
		const { address, validations } = this.state;
		const newValidations = Object.keys(validations).reduce(
			(_newValidations, key) => {
				// 1: valid; -1: invalid
				_newValidations[key] = address[key] === '' ? -1 : 1;

				if (isValid) {
					isValid = _newValidations[key] === 1;
				}

				return _newValidations;
			},
			{}
		);

		this.setState({
			validations: newValidations,
		});

		return isValid;
	};

	render() {
		const {
			layoutContext: { isMobile },
		} = this.props;

		const { mask, address, validations } = this.state;
		const accountInfo = Session.getAccountInfo();

		const billingDay = Session.getInternalAccountInfo().BillingDayOfMonth;
		const nextBillingDate = Util.getBillingDate(billingDay, 'numeric');
		const ageOfAccount = Utils.Util.getAgeOfAccountInDays(
			accountInfo.customerDetails?.creationDate
		);
		const isPayGo = accountInfo.planDetails.planId === '381';

		let header = '';
		const text = (
			<>
				You can reactivate your account anytime before then on the subscription
				settings page. Any remaining account balance greater than $25 will be
				refunded to you via check. If you have less than $25 in your balance
				please{' '}
				<Link
					href="https://help.stamps.com/hc/en-us/requests/new"
					target="_blank"
				>
					contact our Customer Care Team
				</Link>{' '}
				to request a refund.
			</>
		);

		if (isPayGo) {
			header = `If you cancel today, you'll permanently lose access to your account, including your history, contacts, and prints after ${nextBillingDate}.`;
		} else if (ageOfAccount <= 30) {
			header = `If you cancel today, you'll permanently lose access to your account, including your history, contacts, and prints after the last day of your risk-free trial on ${nextBillingDate}.`;
		} else {
			header = `If you cancel today, you'll permanently lose access to your account, including your history, contacts, and prints after the last day of your current billing cycle on ${nextBillingDate}.`;
		}

		return (
			<CancelAccountContext.Consumer>
				{context2 => {
					this.cancelAccountContext = context2;
					return (
						<div className="account-cancel-account-address-verification">
							<h1 className="primary-header">Address Verification</h1>
							<div className="p-grid">
								<div className="p-col-12 cancel-account-warning-message-container">
									<InlineMessage
										header={header}
										text={text}
										type="warning"
										dataqa="cancel-account-warning-message"
									/>
								</div>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="First Name"
									uiElementConfig={{
										onChange: this.onFieldChange,
										value: address.firstName,
										dataqa: 'firstname',
										name: 'firstName',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="Last Name"
									uiElementConfig={{
										onChange: this.onFieldChange,
										value: address.lastName,
										dataqa: 'lastname',
										name: 'lastName',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="Address 1"
									isInputInvalid={validations.addressLine1 === -1}
									showErrorBelow={isMobile}
									errorMessage="This field is required"
									uiElementConfig={{
										onBlur: this.onInputTextBlur,
										onChange: this.onFieldChange,
										maxLength: '50',
										value: address.addressLine1,
										dataqa: 'address1',
										name: 'addressLine1',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="Address 2"
									uiElementConfig={{
										onChange: this.onFieldChange,
										maxLength: '50',
										value: address.addressLine2,
										dataqa: 'address2',
										name: 'addressLine2',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="City"
									isInputInvalid={validations.city === -1}
									showErrorBelow={isMobile}
									errorMessage="This field is required"
									uiElementConfig={{
										onBlur: this.onInputTextBlur,
										onChange: this.onFieldChange,
										maxLength: '28',
										value: address.city,
										dataqa: 'city',
										name: 'city',
									}}
								/>
								<WrappedDropdownWithLabel
									className="p-col-6"
									label="State"
									isInputInvalid={validations.stateProvince === -1}
									showErrorBelow={isMobile}
									errorMessage="This field is required"
									uiElementConfig={{
										optionLabel: 'name',
										optionValue: 'abbreviation',
										options: States,
										value: address.stateProvince,
										onChange: this.onFieldChange,
										dataqa: 'state',
										name: 'stateProvince',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-6"
									label="Zip Code"
									isInputInvalid={validations.postalCode === -1}
									showErrorBelow={isMobile}
									errorMessage="This field is required"
									uiElementConfig={{
										onBlur: this.onInputTextBlur,
										onChange: this.onFieldChange,
										maxLength: '5',
										value: address.postalCode,
										dataqa: 'zip',
										name: 'postalCode',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="Phone Number"
									uiElementConfig={{
										onChange: this.onFieldChange,
										value: address.phone,
										dataqa: 'phone',
										name: 'phone',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12"
									label="Company"
									uiElementConfig={{
										onChange: this.onFieldChange,
										value: address.companyName,
										dataqa: 'company',
										name: 'companyName',
									}}
								/>
								<div className="p-col-12">
									<WrappedButton
										label="Confirm Address & Cancel Account"
										onClick={this.onConfirmAddressClick}
										dataqa="confirm-address"
									/>
								</div>
							</div>
							{mask && <UI.Spinner msg="Canceling Account" />}
						</div>
					);
				}}
			</CancelAccountContext.Consumer>
		);
	}
}

export default withLayoutAndModalContexts(AddressVerification);
